import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AlertCircle, X, Calendar, ClipboardList } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/authContext";

interface PendingApprovals {
  leaves: number;
  compOffs: number;
  total: number;
}

interface PendingApprovalsNotificationProps {
  variant?: "banner" | "badge";
  onDismiss?: () => void;
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
}

export default function PendingApprovalsNotification({
  variant = "banner",
  onDismiss,
  autoRefresh = true,
  refreshInterval = 30000, // 30 seconds
}: PendingApprovalsNotificationProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState<PendingApprovals | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(false);
  const [error, setError] = useState<string>("");

  const fetchPendingApprovals = async () => {
    // Only fetch if user is admin
    if (user?.role !== "admin") {
      return;
    }

    try {
      setIsLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/admin/pending-approvals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setPendingApprovals(response.data.pendingApprovals);
      }
    } catch (error: any) {
      console.error("Error fetching pending approvals:", error);
      setError("Failed to fetch pending approvals");
      // Don't show error to user, just fail silently
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch if user is admin
    if (user?.role !== "admin") {
      return;
    }

    fetchPendingApprovals();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchPendingApprovals();
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, user?.role]);

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleViewLeaves = () => {
    navigate("/leaves");
  };

  const handleViewCompOffs = () => {
    navigate("/compoff");
  };

  // Only show for admin users
  if (user?.role !== "admin") {
    return null;
  }

  // Don't render if dismissed or no pending approvals
  if (isDismissed || !pendingApprovals || pendingApprovals.total === 0) {
    return null;
  }

  if (variant === "badge") {
    // Badge variant for header
    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewLeaves}
          className="relative"
        >
          <Calendar className="h-4 w-4 mr-1" />
          Leaves
          {pendingApprovals.leaves > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-orange-500 text-white rounded-full">
              {pendingApprovals.leaves}
            </span>
          )}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewCompOffs}
          className="relative"
        >
          <ClipboardList className="h-4 w-4 mr-1" />
          Comp-Off
          {pendingApprovals.compOffs > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-orange-500 text-white rounded-full">
              {pendingApprovals.compOffs}
            </span>
          )}
        </Button>
      </div>
    );
  }

  // Banner variant for dashboard
  return (
    <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-orange-900">
              Pending Approvals Required
            </h3>
            <button
              onClick={handleDismiss}
              className="text-orange-600 hover:text-orange-800 transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-sm text-orange-800 mt-1">
            You have <span className="font-semibold">{pendingApprovals.total}</span> pending{" "}
            {pendingApprovals.total === 1 ? "approval" : "approvals"}:
          </p>
          <div className="flex flex-wrap gap-4 mt-3">
            {pendingApprovals.leaves > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewLeaves}
                className="border-orange-300 text-orange-900 hover:bg-orange-100"
              >
                <Calendar className="h-4 w-4 mr-2" />
                {pendingApprovals.leaves} Leave{" "}
                {pendingApprovals.leaves === 1 ? "Request" : "Requests"}
              </Button>
            )}
            {pendingApprovals.compOffs > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewCompOffs}
                className="border-orange-300 text-orange-900 hover:bg-orange-100"
              >
                <ClipboardList className="h-4 w-4 mr-2" />
                {pendingApprovals.compOffs} Comp-Off{" "}
                {pendingApprovals.compOffs === 1 ? "Request" : "Requests"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

